import { Box, Button, ButtonGroup, Flex, HStack, Icon, Text, useToast } from '@chakra-ui/react';
import { useState, FormEventHandler, useCallback, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Download, Info } from 'lucide-react';
import capitalize from 'lodash-es/capitalize.js';
import { CsvRecord, ValidationRules } from '@duke-hq/csv-parser/CsvTypes';
import { defaultValidationRules } from '@duke-hq/csv-parser/CsvUtils';
import { CsvImporter } from '@duke-hq/csv-parser/components/CsvImporter';
import { CsvDownloaderHandler } from '@duke-hq/csv-parser/components/CsvDownloaderHandler';
import { CsvSubmissionSummary } from '@duke-hq/csv-parser/components/CsvSubmissionSummary';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { TeamSummaryErrorTable } from '@duke-hq/app-provider-web/tenant/Team/TeamSummaryErrorTable';
import {
  ConsultantRoleSchema,
  type ConsultantRoleType,
} from '@duke-hq/svc-api/provider/util/consultants';
import { DialogBox } from '@duke-hq/ui/components/DialogBox/DialogBox';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { useProviderEmailDomains } from '../../hooks/useProviderEmailDomains';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';

type CsvError = Record<string, string>;

type Consultant = {
  firstName: string;
  lastName: string;
  email: string;
  role?: ConsultantRoleType;
};

const SAMPLE_CSV_TEMPLATE = ['john.smith', 'jane.taylor', 'michael.jones', 'susan.miller'];

const getProviderEmailDomain = (email: string, allowedDomain: string) => {
  return `${email}@${allowedDomain}`;
};

export function TeamBulkUsers({ onClose }: { onClose?: () => void }) {
  const [formData, setFormData] = useState<Consultant[]>([]);
  const [csvHasErrors, setCsvHasErrors] = useState<boolean>(false);
  const [csvErrors, setCsvErrors] = useState<CsvError[]>([]);
  const [csvConsultants, setCsvConsultants] = useState<Consultant[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [hasCsvData, setHasCsvData] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<{
    validRows: number;
    errorsRows: number;
    isValidated: boolean;
  }>({
    validRows: 0,
    errorsRows: 0,
    isValidated: false,
  });

  const toast = useToast();
  const auth = useAuthTenant();
  const utils = trpcTenant.useUtils();
  const { allowedDomains, defaultDomain } = useProviderEmailDomains();

  const sampleCsv = useMemo<{ first_name: string; last_name: string; email: string }[]>(() => {
    if (defaultDomain == null) {
      return [];
    }

    return SAMPLE_CSV_TEMPLATE.map((username) => {
      const [first_name = '', last_name = ''] = username.split('.');
      return {
        first_name: capitalize(first_name),
        last_name: capitalize(last_name),
        email: getProviderEmailDomain(username, defaultDomain),
      };
    });
  }, [defaultDomain]);

  const createBulkConsultant = trpcTenant.users.bulkUsers.useMutation({
    onSuccess: ({ errors, consultants }) => {
      if (errors?.length) {
        setCsvErrors(
          errors.map((error) => ({
            ...error,
            reason: error.reason ?? 'Unknown error',
          })),
        );
      }

      if (consultants?.length) {
        setCsvConsultants(consultants);

        toast({
          title: t`Consultants created successfully.`,
          description: t`${consultants.length} consultants have been created successfully.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    onSettled: () => {
      if (csvConsultants.length > 0) {
        void utils.users.listMembersWithPagination.refetch();
      }
    },
  });

  const handleProcessedCsvData = useCallback((data: CsvRecord[]) => {
    const importedMembers: Consultant[] = data.map((consultant) => ({
      firstName: consultant.first_name ?? '',
      lastName: consultant.last_name ?? '',
      email: consultant.email ?? '',
      role: ConsultantRoleSchema.enum.CONSULTANT,
    }));

    setFormData(importedMembers);
    setHasCsvData(importedMembers.length > 0);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setCsvHasErrors(false);
    setShowSummary(false);
  }, []);

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault();

      if (formData.length === 0) {
        toast({
          title: t`No valid data to submit.`,
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setCsvConsultants([]);
      setCsvErrors([]);
      setCsvHasErrors(false);

      const consultantsWithRole = formData.map((consultant) => ({
        ...consultant,
        role: ConsultantRoleSchema.enum.CONSULTANT,
      }));

      await createBulkConsultant.mutateAsync(consultantsWithRole);
    },
    [formData, toast, createBulkConsultant],
  );

  const handleTotalRows = useCallback(
    ({
      validRows,
      errorsRows,
      isValidated,
    }: {
      validRows: number;
      errorsRows: number;
      isValidated: boolean;
    }) => {
      setTotalRows({
        validRows,
        errorsRows,
        isValidated: (validRows > 0 && errorsRows === 0) ?? isValidated,
      });
    },
    [],
  );

  const teamBulkUsersValidationRules: ValidationRules = useMemo(
    () => ({
      first_name: defaultValidationRules.first_name,
      last_name: defaultValidationRules.last_name,
      email: defaultValidationRules.email,
    }),
    [],
  );

  const teamBulkUsersColumnDisplayNames = useMemo(
    () => ({
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
    }),
    [],
  );

  const memoizedErrorRows = useMemo(() => {
    return csvErrors.map((err) => ({
      firstName: err.firstName ?? '',
      lastName: err.lastName ?? '',
      email: err.email ?? '',
      reason: err.reason ?? '',
    }));
  }, [csvErrors]);

  const sampleCsvDownloader = (
    <CsvDownloaderHandler type="link" filename={`users-sample`} data={sampleCsv}>
      <Text
        as="span"
        display="inline-flex"
        alignItems="center"
        color="brand.500"
        fontWeight="bold"
        _hover={{ textDecoration: 'underline', color: 'brand.700' }}
      >
        <Trans>CSV template</Trans>
        <Download size={16} style={{ marginLeft: '4px' }} />
      </Text>
    </CsvDownloaderHandler>
  );

  const csvInstructions = (
    <Box as="ol" listStyleType="decimal" pl={4} display="flex" flexDirection="column" gap={2}>
      <Box as="li" key={0} fontSize="sm">
        <Text as="span">
          <Trans>Download the</Trans> {sampleCsvDownloader}{' '}
          <Trans>and add your users details</Trans>
        </Text>
      </Box>
      <Box as="li" key={1} fontSize="sm">
        <Text as="span">
          <Trans>Upload your populated CSV template below</Trans>
        </Text>
      </Box>
      <Box as="li" key={2} fontSize="sm">
        <Text as="span">
          <Trans>Review and confirm the list of users before sending the invites.</Trans>
        </Text>
      </Box>
    </Box>
  );

  return (
    <>
      <Box as="form" onSubmit={handleSubmit} bg="bg.surface" borderRadius="md">
        <FormMainContainer>
          <FormContentContainer>
            <CsvImporter
              allowedDomains={allowedDomains}
              validationRules={teamBulkUsersValidationRules}
              columnDisplayNames={teamBulkUsersColumnDisplayNames}
              csvInstructions={csvInstructions}
              onDataProcessed={handleProcessedCsvData}
              onTotalRows={handleTotalRows}
            />

            {totalRows.validRows > 0 ? (
              <HStack
                w="full"
                justify="space-between"
                bg="bg.subtle"
                px={4}
                py={2}
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.emphasis"
                borderStyle="solid"
                mt={4}
              >
                <HStack spacing={1}>
                  <Icon as={Info} mr={1} boxSize="6" />
                  <HStack w="full" align="center" spacing="10px">
                    <Text fontSize="2xl" color="brand.500" fontWeight="bold">
                      {totalRows.validRows}
                    </Text>
                    <Text fontSize="sm">
                      users will be invited to join {auth?.user.tenant?.name}
                    </Text>
                  </HStack>
                </HStack>
              </HStack>
            ) : null}
          </FormContentContainer>

          <FormFooterContainer>
            <Flex justify={'flex-end'} gap={2}>
              <Button type="button" variant="ghost" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>

              {(csvConsultants.length > 0 || csvErrors.length > 0) && (
                <Button type="button" bg={'pink.500'} onClick={() => setShowSummary(true)}>
                  <Trans>Summary</Trans>
                </Button>
              )}

              <Button
                type="submit"
                right={0}
                isLoading={createBulkConsultant.isPending}
                loadingText="Submitting"
                isDisabled={!totalRows.isValidated || !hasCsvData || createBulkConsultant.isPending}
              >
                <Trans>Invite team members</Trans>
              </Button>
            </Flex>
          </FormFooterContainer>
        </FormMainContainer>
      </Box>

      <DialogBox
        motionPreset="slideInBottom"
        isCentered
        isOpen={csvHasErrors || showSummary}
        onClose={handleCloseDialog}
        header={<Trans>Consultants creation results</Trans>}
        body={
          <CsvSubmissionSummary
            successCount={csvConsultants.length}
            errorCount={csvErrors.length}
            entityLabel={t`Consultant`}
            errorTable={<TeamSummaryErrorTable errors={memoizedErrorRows} />}
          />
        }
        footer={
          <ButtonGroup>
            <Button type="button" onClick={handleCloseDialog}>
              <Trans>Close</Trans>
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
}
